<template>
  <v-container class="service-panel pa-0" fluid>
    <loading-box v-if="MachineServiceHistories == null" :loading="true" />

    <div v-else class="mt-4 pb-3">
      <!-- Open Jobs Section -->
      <div class="section-header d-flex align-center mb-3">
        <v-icon color="primary" class="mr-2" size="small">mdi-clipboard-text-clock-outline</v-icon>
        <h3 class="section-title">
          {{ $t("text.openjobs") }}
          <v-chip v-if="openJobs && openJobs.length > 0" size="small" color="primary" variant="flat" class="ml-2 count-chip">
            {{ openJobs.length }}
          </v-chip>
        </h3>
        <v-spacer />
        <v-btn color="primary" variant="flat" size="small" class="order-service-btn" @click="newjobdialog = true">
          <v-icon start size="small">mdi-plus</v-icon>
          {{ $t("text.orderservice") }}
        </v-btn>
      </div>

      <!-- Empty state for open jobs -->
      <v-card v-if="openJobs && openJobs.length === 0" variant="outlined" class="text-center pa-6 mb-6 empty-state-card">
        <v-icon size="48" color="grey-lighten-1">mdi-check-circle-outline</v-icon>
        <div class="text-body-1 text-medium-emphasis mt-2">{{ $t("text.openjobs") }}: 0</div>
      </v-card>

      <!-- Open Job Cards -->
      <OpenJobCard v-for="item in openJobs" :key="'open-' + item.Id" :job="item" @open="openJob">
      </OpenJobCard>

      <!-- History Section -->
      <v-row no-gutters align="center" class="mb-3 mt-6">
        <v-icon color="grey-darken-1" class="mr-2" size="small">mdi-history</v-icon>
        <h3 class="section-title">{{ $t("text.closed_jobs") }}</h3>
        <v-chip v-if="closedJobs && closedJobs.length > 0" size="small" variant="outlined" class="ml-3 count-chip">
          {{ closedJobs.length }}
        </v-chip>
        <v-divider class="ml-3"></v-divider>
      </v-row>

      <!-- Empty state for history -->
      <v-card v-if="!closedJobs || closedJobs.length === 0" variant="outlined"
        class="text-center pa-6 mb-6 empty-state-card">
        <v-icon size="48" color="grey-lighten-1">mdi-archive-outline</v-icon>
        <div class="text-body-1 text-medium-emphasis mt-2">{{ $t("text.closed_jobs") }}: 0</div>
      </v-card>

      <!-- History Cards -->
      <v-expansion-panels v-else multiple class="history-panels">
        <HistoricJobCard v-for="item in closedJobs" :key="'hist-' + item.Id" :history-item="item"
          :show-price="showPrice" :is-phone="isPhone" @open="openJob">
        </HistoricJobCard>
      </v-expansion-panels>
    </div>

    <new-job-dialog :machine="machine" :machines="[machine]" v-if="newjobdialog" @exit="closeNewJobDialog" />

    <JobDialog />

    <v-snackbar v-model="snackbar" color="primary" rounded>
      <v-icon dark small class="mr-2">mdi-check-circle</v-icon>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>


<script>
import { useDisplay } from "vuetify";
import http from "@/api/client";
import JobDialog from "@/components/Dialogs/JobDialog.vue";
import NewJobDialog from "@/components/Dialogs/NewJobDialogEx.vue";
import LoadingBox from "@/components/LoadingBox.vue";
import OpenJobCard from "@/components/Cards/OpenJobCard.vue";
import HistoricJobCard from "@/components/Cards/HistoricJobCard.vue";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";

export default {
  name: "ServicePanel",
  mixins: [AuthStoreMixin],

  setup() {
    const { xs } = useDisplay();

    return {
      isPhone: xs,
    };
  },

  components: {
    "loading-box": LoadingBox,
    NewJobDialog,
    JobDialog,
    OpenJobCard,
    HistoricJobCard,
  },

  props: {
    machine: { type: Object, default: null },
  },

  data() {
    return {
      snackbar: false,
      snackbarMessage: null,
      openJobs: null,
      machineSelection: null,
      loading: false,
      newjobdialog: false,
      selectedJob: null,
      MachineServiceHistories: null,
    };
  },

  mounted() {
    if (this.machine) this.updateServiceHistories(this.machine);
    else this.$emit("updateMachine");
  },

  methods: {
    closeNewJobDialog() {
      this.newjobdialog = false;
      this.snackbarMessage = null;
      this.snackbar = false;
      this.updateServiceHistories(this.machine);
    },

    updateServiceHistories(machine) {
      if (!machine) return;
      this.loading = true;
      http()
        .get("api2/customer/machines/history/" + machine.Id)
        .then((response) => {
          this.MachineServiceHistories = response.data;
          if (this.closedJobs && this.closedJobs.length > 0)
            this.selectedJob = this.closedJobs[0];
          this.loading = false;
        });

      http()
        .get("api2/customer/jobs/openmachinejobs/" + machine.Id)
        .then((response) => {
          this.openJobs = response.data;
        });
    },

    openJob(item) {
      this.$router.push({ params: { jobId: item.Id } });
    },

  },

  computed: {
    closedJobs() {
      if (
        this.MachineServiceHistories != null &&
        this.MachineServiceHistories.length > 0
      ) {
        return this.MachineServiceHistories.filter(
          (e) => e.Job?.StatusId > 30 && e.Job?.StatusId <= 100
        ).sort((a, b) => b.Id - a.Id);
      } else {
        return null;
      }
    },

    showPrice() {
      return this.canShowPrices;
    },

    partsHeader() {
      var headers = [
        {
          text: this.$t("text.article"),
          value: "Article.ArticleDescription",
        },
        {
          text: this.$t("text.amount"),
          value: "ArticleQtyOrdered",
        },
      ];
      if (this.showPrice) {
        headers.push({
          text: this.$t("text.price"),
          value: "ArticlePriceOut",
        });
      }
      return headers;
    },
  },

  watch: {
    machine(val) {
      this.updateServiceHistories(val);
    },
  },
};
</script>


<style scoped>
.service-panel {
  max-width: 900px;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 23px;
  font-weight: 400;
  line-height: 1.25;
}

.count-chip {
  min-width: 30px;
  justify-content: center;
}

.order-service-btn {
  border-radius: 4px;
  box-shadow: none !important;
  font-weight: 700;
  letter-spacing: 0;
}

.history-panels {
  display: block;
}

.empty-state-card {
  border-radius: 8px !important;
  border-style: dashed !important;
}
</style>
